import React from 'react'
import { toast } from 'react-toastify'
import { Yup as y } from '@/services/yup'

import api from '@/services/api'
import apiEndPoints from '@/consts/apiEndPoints'
import { ORDER_TYPES_LABELS } from '@/_mock/orders'

import { Button, confirmationDialog } from '@/components'
import { Form, FormNumberField } from '@/components/CoForm'
import {
  Box,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core'

import { StyledTableContainer } from './styles'
import { SpecialCompanies } from './SpecialCompanies'

// ---------------------------------------------

const limitSchema = y
  .number()
  .transform((value, originalValue) => {
    return originalValue === '' ? undefined : value
  })
  .typeError('Informe um número válido')
  .required('Informe o limite')
// .moreThan(0, 'O valor deve ser maior que 0')

const DailyLimitsSchema = y.object().shape({
  specialCompanies: y.array().of(y.string()).default([]),
  limits: y.lazy((data) =>
    y.object(
      Object.keys(data || {}).reduce((acc, key) => {
        acc[key] = y.object().shape({
          limite_quantidade_fracionada_diaria: limitSchema,
          limite_quantidade_fracionada_mq_diaria: limitSchema,
        })

        return acc
      }, {})
    )
  ),
})

// ----------------------------------------------------------

export function DailyLimits({ dailyLimits }) {
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const formRef = React.useRef(null)

  const defaultValues = React.useMemo(() => {
    const limits = dailyLimits.reduce((acc, dailyLimit) => {
      acc[dailyLimit.id_controle] = {
        limite_quantidade_fracionada_diaria:
          dailyLimit.limite_quantidade_fracionada_diaria,
        limite_quantidade_fracionada_mq_diaria:
          dailyLimit.limite_quantidade_fracionada_mq_diaria,
      }

      return acc
    }, {})

    return {
      specialCompanies: [],
      limits,
    }
  }, [dailyLimits])

  async function updateDailyLimits({ limits, specialCompanies }) {
    setIsSubmitting(true)

    try {
      await api.post(apiEndPoints.admin.dailyLimits, {
        daily_limits: dailyLimits.map((dl) => ({
          ...dl,
          ...limits[dl.id_controle],
        })),
        special_companies: specialCompanies,
      })

      toast.success('Limites atualizados com sucesso')
    } catch (error) {
      toast.error('Falha ao atualizar os limites')
    } finally {
      setIsSubmitting(false)
    }
  }

  async function handleSubmit(data) {
    const confirm = await confirmationDialog.open(
      'Confirmar',
      'Confirma alteração dos limites diários?'
    )

    confirmationDialog.close()
    if (confirm) updateDailyLimits(data)
  }

  return (
    <Form
      formRef={formRef}
      initialData={defaultValues}
      schema={DailyLimitsSchema}
      onSubmit={handleSubmit}
    >
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <StyledTableContainer>
            <Table size="medium">
              <TableHead>
                <TableRow>
                  <TableCell colSpan={3}>
                    <Typography
                      color="primary"
                      variant="body1"
                      style={{ textTransform: 'uppercase', fontWeight: 600 }}
                    >
                      Coletas Fracionadas
                    </Typography>
                  </TableCell>
                </TableRow>

                <TableRow>
                  <TableCell>TIPOS</TableCell>
                  <TableCell style={{ width: 150 }}>VOLUMES</TableCell>
                  <TableCell style={{ width: 150 }}>QUANTIDADE m²</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {dailyLimits.map((dailyLimit) => {
                  const formName = dailyLimit.id_controle
                  const orderTypes = dailyLimit.tipos.split(',')

                  return (
                    <TableRow key={dailyLimit.id_controle}>
                      <TableCell>
                        {orderTypes.map((orderType) => (
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            key={orderType}
                          >
                            ({orderType}) - {ORDER_TYPES_LABELS[orderType]}
                          </Typography>
                        ))}
                      </TableCell>

                      <TableCell>
                        <FormNumberField
                          fullWidth
                          name={`limits.${formName}.limite_quantidade_fracionada_diaria`}
                          inputProps={{ inputMode: 'numeric' }}
                        />
                      </TableCell>

                      <TableCell>
                        <FormNumberField
                          fullWidth
                          name={`limits.${formName}.limite_quantidade_fracionada_mq_diaria`}
                          inputProps={{ inputMode: 'numeric' }}
                        />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </StyledTableContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box display="flex" flexDirection="column" marginTop="1rem">
            <Typography
              color="primary"
              variant="body1"
              style={{
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              Transportadoras Especiais
            </Typography>

            <Divider style={{ marginTop: '1rem' }} />
            <SpecialCompanies name="specialCompanies" />
          </Box>
        </Grid>
      </Grid>

      <Box marginTop="1rem">
        <Divider style={{ margin: '1rem 0' }} />

        <Button
          loading={isSubmitting}
          style={{ minWidth: '20%' }}
          type="submit"
          variant="contained"
          color="primary"
          // size="large"
        >
          Salvar
        </Button>
      </Box>
    </Form>
  )
}
